import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import type { EmailPreview } from "@/lib/emailApi";

interface Props {
  preview: EmailPreview;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

// Step 2 of the flow: recipients / type / subject / rendered template, then an explicit confirmation checkbox before sending.
export function EmailPreviewPanel({ preview, busy, onBack, onConfirm }: Props) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const bulk = preview.target_type === "bulk";
  return (
    <div className="space-y-5" data-testid="admin-email-preview">
      <h2 className="text-lg font-bold text-white">{t("adminEmail.previewTitle")}</h2>
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        {[
          ["recipients", String(preview.recipient_count), "admin-email-preview-count"],
          ["type", t(`adminEmail.category.${preview.category}`), "admin-email-preview-category"],
          ["target", t(`adminEmail.target_type.${preview.target_type}`), "admin-email-preview-target"],
          ["subjectShort", preview.subject, "admin-email-preview-subject"],
        ].map(([k, v, id]) => (
          <div key={k} className="rounded-xl border border-white/10 bg-black/30 p-3">
            <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">{t(`adminEmail.${k}`)}</dt>
            <dd className="mt-1 truncate font-semibold text-white" data-testid={id} title={v}>{v}</dd>
          </div>
        ))}
      </dl>
      <iframe title="email preview" srcDoc={preview.html} sandbox="allow-same-origin" className="h-[420px] w-full rounded-xl border border-cyan-300/30 bg-[#050b16]" data-testid="admin-email-preview-frame" />
      {!preview.configured && <p className="text-xs text-yellow-200" data-testid="admin-email-preview-unconfigured">{t("adminEmail.notConfigured")}</p>}
      {preview.warnings?.includes("CTA_URL_MISSING") && <p className="text-xs text-yellow-200" data-testid="admin-email-preview-ctamissing">{t("adminEmail.official.ctaMissing")}</p>}
      {preview.welcome_already_sent_at && <p className="rounded-lg border border-lime-300/40 bg-lime-300/10 px-3 py-2 text-xs text-lime-100" data-testid="admin-email-preview-welcome-sent">{t("adminEmail.official.welcomeAlreadySent", { date: new Date(preview.welcome_already_sent_at).toLocaleString() })}</p>}
      <label className="flex items-start gap-3 text-sm text-white">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1 h-4 w-4 accent-cyan-300" data-testid="admin-email-confirm-checkbox" />
        <span>{bulk ? t("adminEmail.confirmBulk", { count: preview.recipient_count }) : t("adminEmail.confirmSingle")}</span>
      </label>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onBack} disabled={busy} data-testid="admin-email-back-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm text-white hover:border-white/50">
          <ArrowLeft className="h-4 w-4" />{t("adminEmail.back")}
        </button>
        <button type="button" onClick={onConfirm} disabled={!confirmed || busy || preview.recipient_count === 0} data-testid="admin-email-send-btn" className="cta-hero inline-flex items-center gap-2 px-6 py-2 text-sm">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{t("adminEmail.sendBtn", { count: preview.recipient_count })}
        </button>
      </div>
    </div>
  );
}
